// components/Dashboard/DieselCard.jsx — per-location version
// Receives raw diesel transaction records for one location (sorted newest-first).
import React from "react"; // eslint-disable-line no-unused-vars
import { Text } from "@fluentui/react-components";
import { formatDate, safeNum } from "../../utils/helpers";
import { RadialBarChart, RadialBar, ResponsiveContainer } from "recharts";

const ROW_COLORS = ["#0066FF", "#107C10", "#D13438", "#986F0B"];

export default function DieselCard({ location, records = [], style = {} }) {
  if (!records.length) return null;

  const latest = records[0]; // most recent week
  const maxClosing = Math.max(...records.map((r) => safeNum(r.closingStock)), 1);
  const closing    = safeNum(latest.closingStock);
  const efficiency = Math.min(100, (closing / maxClosing) * 100);
  const gaugeColor = efficiency > 50 ? "#107C10" : efficiency > 25 ? "#FF8C00" : "#D13438";
  const gaugeData  = [{ value: efficiency, fill: gaugeColor }];

  const rows = [
    { label: "Opening Stock", value: latest.openingStock, color: ROW_COLORS[0] },
    { label: "Supply",        value: latest.supply,       color: ROW_COLORS[1] },
    { label: "Consumption",   value: latest.consumption,  color: ROW_COLORS[2] },
    { label: "Closing Stock", value: latest.closingStock, color: ROW_COLORS[3] },
  ];

  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #EDEBE9", ...style }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#986F0B,#D4921A)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>⛽</div>
        <div>
          <Text weight="semibold" size={400} style={{ color: "#201F1E", display: "block" }}>{location}</Text>
          <Text style={{ fontSize: 11, color: "#8A8886" }}>Week: {formatDate(latest.weekEnding)}</Text>
        </div>
      </div>

      {/* Gauge */}
      <div style={{ height: 90, marginBottom: 4 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart innerRadius="70%" outerRadius="100%" data={gaugeData} startAngle={180} endAngle={0}>
            <RadialBar dataKey="value" cornerRadius={6} background={{ fill: "#F3F2F1" }} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div style={{ textAlign: "center", marginTop: -32 }}>
          <Text weight="bold" style={{ fontSize: 18, color: "#201F1E" }}>{efficiency.toFixed(0)}%</Text>
          <Text style={{ display: "block", fontSize: 10, color: "#8A8886" }}>Stock Level</Text>
        </div>
      </div>

      {/* Stock breakdown rows */}
      {rows.map((r) => (
        <div key={r.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #F3F2F1" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: r.color, flexShrink: 0 }} />
            <Text style={{ fontSize: 12, color: "#605E5C" }}>{r.label}</Text>
          </div>
          <Text weight="semibold" style={{ fontSize: 13, color: "#201F1E" }}>
            {r.value != null ? `${Number(r.value).toLocaleString()} L` : "—"}
          </Text>
        </div>
      ))}
    </div>
  );
}
